import React, { useLayoutEffect, useRef, useState } from 'react';
import clsx from 'clsx';

import CanvasIcon from '@/assets/images/canvas/canvas-image.svg?react';

interface CanvasImageUploadProps {
  className?: string;
  onClick?: () => void;
  type?: 'default' | 'selected' | 'active';
  text?: React.ReactNode | string;
  aspect?: string;
  iconSizePercent?: number;
}

export const CanvasImageUpload: React.FC<CanvasImageUploadProps> = ({
  className = '',
  onClick,
  type = 'default',
  text,
  aspect = '4/3',
  iconSizePercent = 38.5,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [corner, setCorner] = useState<{ rx: number; ry: number }>({ rx: 0, ry: 0 });
  const [hovered, setHovered] = useState(false);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      const width = el.clientWidth;
      const height = el.clientHeight;
      if (!width || !height) return;
      const cs = getComputedStyle(el);
      const br = parseFloat(cs.borderTopLeftRadius || '0') || 0;
      setCorner({ rx: (br / width) * 100, ry: (br / height) * 100 });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const handleClick = () => {
    if (onClick) onClick();
  };

  const isDashed = type === 'default' || type === 'selected';

  return (
    <div
      ref={containerRef}
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={clsx(
        `relative flex h-full max-h-56 w-full max-w-60 aspect-${aspect} box-border flex-col items-center justify-center gap-4 overflow-hidden rounded-xl p-4 transition-all`,
        className,
      )}
      style={{ backgroundColor: hovered ? '#CFEBDF' : undefined }}
    >
      {/* One SVG, two rects: crossfade dashed -> solid on hover for perfect alignment */}
      <svg
        className="pointer-events-none absolute inset-0 h-full w-full"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        aria-hidden
        style={{ color: '#22c55e' }}
      >
        {/* Dashed state */}
        <rect
          x="0.5"
          y="0.5"
          width="99"
          height="99"
          rx={corner.rx}
          ry={corner.ry}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.1"
          vectorEffect="non-scaling-stroke"
          shapeRendering="crispEdges"
          strokeDasharray={isDashed ? '6 4' : undefined}
          style={{ opacity: isDashed && !hovered ? 1 : 0, transition: 'opacity 150ms ease' }}
        />
        {/* Solid state */}
        <rect
          x="0.5"
          y="0.5"
          width="99"
          height="99"
          rx={corner.rx}
          ry={corner.ry}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.1"
          vectorEffect="non-scaling-stroke"
          shapeRendering="crispEdges"
          style={{ opacity: hovered || !isDashed ? 1 : 0, transition: 'opacity 150ms ease' }}
        />
      </svg>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          aspectRatio: '1 / 1',
          width: `${iconSizePercent}%`,
          borderRadius: '0.75rem',
          backgroundColor: 'rgba(5, 150, 105, 0.10)', // emerald-600 @ 10%
          border: '1px solid #34d399', // emerald-400
          transition: 'all 150ms ease',
          backdropFilter: 'blur(3.2px)',
        }}
      >
        <CanvasIcon style={{ width: '50%', height: '50%' }} />
      </div>

      {text && (
        <p
          className="text-heading-ts w-full truncate overflow-hidden text-center whitespace-nowrap text-gray-600"
          style={{ fontSize: 'clamp(12px, 2vw, 12px)' }}
        >
          {text}
        </p>
      )}
    </div>
  );
};
